import { create } from 'zustand'
import { api } from '../services/api.js'

export const useTreeStore = create((set, get) => ({
  trees: [],
  currentTree: null,
  persons: [],
  relationships: [],
  loading: false,
  error: null,

  // Era Context — the person currently in focus across all views
  // Persisted to localStorage so it survives page refresh
  focalPersonId: localStorage.getItem('gl_focal_person') || null,
  focalPersonData: JSON.parse(localStorage.getItem('gl_focal_person_data') || 'null'),
  setFocalPerson: (id, personData) => {
    if (!id) {
      localStorage.removeItem('gl_focal_person')
      localStorage.removeItem('gl_focal_person_data')
      set({ focalPersonId: null, focalPersonData: null })
      return
    }
    localStorage.setItem('gl_focal_person', id)
    if (personData) localStorage.setItem('gl_focal_person_data', JSON.stringify(personData))
    set({ focalPersonId: id, focalPersonData: personData || get().focalPersonData })
  },

  fetchTrees: async () => {
    set({ loading: true, error: null })
    try {
      const trees = await api.get('/trees')
      set({ trees, loading: false })
    } catch (err) {
      // Keep any previously-loaded trees visible on transient failure
      // rather than clearing the sidebar to "no trees" on a blip.
      console.warn('[trees] fetchTrees failed', err?.message)
      set({ error: err.message, loading: false })
    }
  },

  fetchTree: async (treeId) => {
    set({ loading: true, error: null })

    // Load the three endpoints independently. The old Promise.all
    // pattern meant any single blip (API restart, Wikidata-harvest-
    // induced slow query, flaky network) blanked the whole tree to
    // "0 persons" — the symptom Ruben was hitting repeatedly.
    //
    // Per-call settling means:
    //   - tree metadata failure → keep stale currentTree (header stays)
    //   - persons failure → show the existing persons (or empty if new)
    //   - relationships failure → tree view renders nodes without lines
    //                              rather than blanking entirely
    // Each failure logs so we can still diagnose.
    const [treeRes, personsRes, relationshipsRes] = await Promise.allSettled([
      api.get(`/trees/${treeId}`),
      api.get(`/persons?tree_id=${treeId}`),
      api.get(`/relationships?tree_id=${treeId}`),
    ])

    const tree = treeRes.status === 'fulfilled' ? treeRes.value : get().currentTree
    const persons = personsRes.status === 'fulfilled' ? personsRes.value : (get().persons || [])
    const relationships = relationshipsRes.status === 'fulfilled' ? relationshipsRes.value : (get().relationships || [])

    const failures = [treeRes, personsRes, relationshipsRes].filter(r => r.status === 'rejected')
    if (failures.length > 0) {
      console.warn('[tree] partial load failure', failures.map(f => f.reason?.message || f.reason))
    }

    set({
      currentTree: tree,
      persons,
      relationships,
      loading: false,
      error: failures.length === 3 ? 'Could not load tree — retrying in a moment.' : null,
    })

    // Clear focal person if it belongs to another tree or has been deleted;
    // otherwise the tree view tries to zoom to a ghost id.
    const focalId = get().focalPersonId
    if (focalId && Array.isArray(persons) && persons.length > 0 && !persons.some(p => p.id === focalId)) {
      localStorage.removeItem('gl_focal_person')
      localStorage.removeItem('gl_focal_person_data')
      set({ focalPersonId: null, focalPersonData: null })
    }

    // If everything failed, schedule one auto-retry after a beat —
    // covers the common "pm2 restart finished mid-request" case
    // without the user having to refresh.
    if (failures.length === 3) {
      setTimeout(() => get().fetchTree(treeId), 2500)
    }
  },

  addPerson: async (personData) => {
    const person = await api.post('/persons', personData)
    set(s => ({ persons: [...s.persons, person] }))
    return person
  },

  updatePerson: async (personId, data) => {
    const updated = await api.patch(`/persons/${personId}`, data)
    set(s => ({
      persons: s.persons.map(p => p.id === personId ? updated : p)
    }))
    return updated
  },

  addRelationship: async (data) => {
    const rel = await api.post('/relationships', data)
    set(s => ({ relationships: [...s.relationships, rel] }))
    return rel
  },

  removeRelationship: async (relId) => {
    await api.delete(`/relationships/${relId}`)
    set(s => ({ relationships: s.relationships.filter(r => r.id !== relId) }))
  }
}))
